import { useState, useRef, useEffect } from 'react';
import {
  Maximize,
  Minimize,
  Power,
  Keyboard,
  Activity,
  Monitor,
  ChevronDown,
  Clipboard,
  ClipboardX,
  Command,
} from 'lucide-react';
import { useRdpStore, type RdpStats, type MonitorInfo } from '../../stores/rdpStore';
import { peerConnection } from '../../services/peerConnection';
import { useSettingsStore } from '../../stores/settingsStore';
import { logger } from '@/lib/logger';

type ScaleMode = 'fit' | 'fill' | 'native';

interface RdpToolbarProps {
  onToggleFullscreen: () => void;
  onDisconnect: () => void;
  onScaleModeChange?: (mode: ScaleMode) => void;
  isFullscreen: boolean;
  stats: RdpStats;
  scaleMode?: ScaleMode;
}

export default function RdpToolbar({
  onToggleFullscreen,
  onDisconnect,
  onScaleModeChange,
  isFullscreen,
  stats,
  scaleMode = 'fit',
}: RdpToolbarProps) {
  const inputEnabled = useRdpStore((s) => s.inputEnabled);
  const setInputEnabled = useRdpStore((s) => s.setInputEnabled);
  const remoteScreenWidth = useRdpStore((s) => s.remoteScreenWidth);
  const remoteScreenHeight = useRdpStore((s) => s.remoteScreenHeight);
  const availableMonitors = useRdpStore((s) => s.availableMonitors);
  const setRemoteScreenDimensions = useRdpStore((s) => s.setRemoteScreenDimensions);
  const clipboardSync = useSettingsStore((s) => s.rdp.clipboardSync);
  const setClipboardSync = useSettingsStore((s) => s.setRdpClipboardSync);

  const [monitorDropdownOpen, setMonitorDropdownOpen] = useState(false);
  const [scaleDropdownOpen, setScaleDropdownOpen] = useState(false);
  const monitorDropdownRef = useRef<HTMLDivElement>(null);
  const scaleDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (monitorDropdownRef.current && !monitorDropdownRef.current.contains(e.target as Node)) {
        setMonitorDropdownOpen(false);
      }
      if (scaleDropdownRef.current && !scaleDropdownRef.current.contains(e.target as Node)) {
        setScaleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitchMonitor = async () => {
    setMonitorDropdownOpen(false);
    try {
      const result = await peerConnection.sendCommand<{
        screenWidth: number;
        screenHeight: number;
        scaleFactor: number;
      }>('rdp_switch_monitor', {});
      setRemoteScreenDimensions(result.screenWidth, result.screenHeight, result.scaleFactor);
    } catch (err) {
      logger.warn('RdpToolbar', 'Failed to switch monitor:', err);
    }
  };

  const handleCtrlAltDel = () => {
    peerConnection.sendCommand('rdp_key_event', {
      key: 'Delete',
      code: 'Delete',
      action: 'press',
      modifiers: { ctrl: true, alt: true, shift: false, meta: false },
    }).catch((err) => {
      logger.warn('RdpToolbar', 'Ctrl+Alt+Del failed:', err);
    });
  };

  const formatBitrate = (bps: number) => {
    if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
    if (bps >= 1_000) return `${(bps / 1_000).toFixed(0)} Kbps`;
    return `${bps} bps`;
  };

  const scaleModeLabels: Record<ScaleMode, string> = {
    fit: 'Fit',
    fill: 'Fill',
    native: '1:1',
  };

  return (
    <div className="flex items-center justify-between px-3 py-1.5 bg-base-300/90 backdrop-blur-sm border-b border-base-content/10 flex-shrink-0 z-10">
      {/* Left: connection info & stats */}
      <div className="flex items-center gap-3 text-xs">
        <span className="text-base-content/60">
          {remoteScreenWidth > 0 ? `${remoteScreenWidth}x${remoteScreenHeight}` : 'Connecting...'}
        </span>
        {stats.fps > 0 && (
          <span className="flex items-center gap-1 text-base-content/50">
            <Activity className="w-3 h-3" />
            {stats.fps} fps
          </span>
        )}
        {stats.bitrate > 0 && (
          <span className="text-base-content/50">{formatBitrate(stats.bitrate)}</span>
        )}
        {stats.latency > 0 && (
          <span className="text-base-content/50">{stats.latency}ms</span>
        )}
      </div>

      {/* Right: controls */}
      <div className="flex items-center gap-1">
        {/* Monitor selector */}
        {availableMonitors.length > 0 && (
          <div className="relative" ref={monitorDropdownRef}>
            <button
              onClick={() => setMonitorDropdownOpen(!monitorDropdownOpen)}
              className="btn btn-ghost btn-xs gap-1"
              title="Switch monitor"
            >
              <Monitor className="w-3.5 h-3.5" />
              <ChevronDown className="w-3 h-3" />
            </button>
            {monitorDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-base-200 border border-base-300 rounded-lg shadow-xl min-w-48 z-20">
                <div className="px-3 py-2 text-xs font-semibold text-base-content/60 border-b border-base-300">
                  Monitors
                </div>
                {availableMonitors.map((m: MonitorInfo) => (
                  <button
                    key={m.id}
                    onClick={handleSwitchMonitor}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-base-300 transition-colors"
                  >
                    <Monitor className="w-3.5 h-3.5" />
                    <span className="flex-1 text-left">
                      {m.name} {m.is_primary && '(Primary)'}
                    </span>
                    <span className="text-base-content/40">
                      {m.width}x{m.height}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Scale mode dropdown */}
        {onScaleModeChange && (
          <div className="relative" ref={scaleDropdownRef}>
            <button
              onClick={() => setScaleDropdownOpen(!scaleDropdownOpen)}
              className="btn btn-ghost btn-xs gap-1"
              title="Scale mode"
            >
              <span className="text-xs">{scaleModeLabels[scaleMode]}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {scaleDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-base-200 border border-base-300 rounded-lg shadow-xl min-w-32 z-20">
                {(['fit', 'fill', 'native'] as ScaleMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => {
                      onScaleModeChange(mode);
                      setScaleDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-base-300 transition-colors ${
                      scaleMode === mode ? 'bg-base-300 font-medium' : ''
                    }`}
                  >
                    {scaleModeLabels[mode]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="divider divider-horizontal mx-0.5 h-4" />

        {/* Clipboard sync toggle */}
        <button
          onClick={() => setClipboardSync(!clipboardSync)}
          className={`btn btn-ghost btn-xs gap-1 ${clipboardSync ? 'text-info' : 'text-base-content/50'}`}
          title={clipboardSync ? 'Clipboard sync on' : 'Clipboard sync off'}
        >
          {clipboardSync ? (
            <Clipboard className="w-3.5 h-3.5" />
          ) : (
            <ClipboardX className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Input toggle */}
        <button
          onClick={() => setInputEnabled(!inputEnabled)}
          className={`btn btn-ghost btn-xs gap-1 ${inputEnabled ? 'text-primary' : 'text-base-content/50'}`}
          title={inputEnabled ? 'Input enabled (click to disable)' : 'Input disabled (click to enable)'}
        >
          <Keyboard className="w-3.5 h-3.5" />
          <span className="text-xs">{inputEnabled ? 'Input On' : 'Input Off'}</span>
        </button>

        {/* Ctrl+Alt+Del */}
        <button
          onClick={handleCtrlAltDel}
          className="btn btn-ghost btn-xs gap-1"
          title="Send Ctrl+Alt+Del"
        >
          <Command className="w-3.5 h-3.5" />
          <span className="text-xs hidden sm:inline">C+A+D</span>
        </button>

        <div className="divider divider-horizontal mx-0.5 h-4" />

        {/* Fullscreen toggle */}
        <button
          onClick={onToggleFullscreen}
          className="btn btn-ghost btn-xs btn-square"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
        </button>

        {/* Disconnect */}
        <button
          onClick={onDisconnect}
          className="btn btn-ghost btn-xs btn-square text-error"
          title="Disconnect"
        >
          <Power className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
